/* eslint-disable max-lines */
import * as React from 'react';
import { flushSync } from 'react-dom';
import { action, autorun, computed, IReactionDisposer, observable } from 'mobx';
import { observer } from 'mobx-react';
import ReactResizeDetector from 'react-resize-detector';

import { KeyboardCodes, KeyboardKeys } from '@app/AppConstants/KeyboardKeys';
import { FunctionGroupIds } from '@app/AppConstants/PersonFunctions';
import { Sizes } from '@app/AppConstants/Sizes';
import { SignalREvents, SignalRGroups } from '@app/AppConstants/SignalR';
import { DutyItem, RosterType } from '@app/Models/WebApiModels';

import {
    IRosterSelectionStrategy,
    RosterSelectionStrategyDefault,
    SelectionStrategyConstructor,
} from '.';

import {
    ActionInitiator,
    getDayWidth,
    IPersonStore,
    IVirtualizedRoster,
    LoadMode,
    NavigateBehavior,
    Range,
    RangeReadyStatus,
    RefreshRosterLineDto,
    RosterElement,
    RosterViewState,
    RosterZoom,
    StoredPersons,
    VirtualizedRosterProps
} from './VirtualizedRosterTypes';

import { appStore } from '@app/AppStore';
import { DutyStore, IDutyStore } from './Stores/DutyStore';
import ArrayHelper from '@app/Helpers/ArrayHelper';
import { applyDecorators } from "@app/Helpers/Decorator";
import { isIOS } from '@app/Helpers/DevicesHelper';

import { PersonsListItem } from '../PersonsListItem';
import RosterLine from '@app/Components/RosterLine';
import { HomeFiltersState } from '@app/Pages/Home/Models';

import Extensions from '@app/Services/Extensions';
import DateTimeService from '@app/Services/DateTimeService';
import { signalRService } from '@app/Services/SignalRService';
import { DutyChangeEvent, dutyChangeService } from '@app/Services/DutyChangeService';

import { TimeoutTimer, VirtualDaysList, VirtualHorizontalExpander } from './Components';
import { modalService } from '../Modal';

enum UpdateStatuses {
    None,
    Pending,
    Ready,
    NeedToUpdate
}

@observer
export default class VirtualizedRoster extends React.Component<VirtualizedRosterProps> implements IVirtualizedRoster {
    private readonly _datesList: React.RefObject<HTMLDivElement> = React.createRef();
    @observable private _personList: React.RefObject<HTMLDivElement> = React.createRef();
    @observable private _scrollWrapper: React.RefObject<HTMLDivElement> = React.createRef();
    private readonly _dutyStore: IDutyStore;
    private readonly _personStore: IPersonStore;
    private _autoDisposer: IReactionDisposer;

    @observable private _dateFrom: Date;
    @observable private _dateTo: Date;
    @observable private _viewportDateFrom: Date | null = null;
    @observable private _viewportDateTo: Date | null = null;
    @observable.shallow private _viewportDays: Array<Date> = [];
    @observable private _viewportPersonIndexFrom: number = -1;
    @observable private _viewportPersonIndexTo: number = -1;
    private _majorMonthDay: Date | null = null;
    private _scrollTop: number = 0;
    private _scrollLeft: number = 0;
    private _gridClientWidth: number = 0;
    private _updateStatus: UpdateStatuses = UpdateStatuses.None;
    @observable private _scheduleEnsureRange: { range: Range<Date>; alwaysCenter?: boolean | null } | null = null;
    @observable private _scheduledRestoreScroll: { top: number | null; left: number | null } | null = null;
    @observable.shallow private _selectedCells: RosterElement[] = [];
    @observable.shallow private _selectedElement: RosterElement | null = null;
    @observable.ref private _copiedDuties: DutyItem[] = [];
    @observable private _autoExpandTimeout: number | null = null;
    @observable private _autoExpandCssClass: string = '';
    @observable private _zoom: RosterZoom;
    private _selectedPersonIdsMap: Map<string, string> = new Map();
    private _timeout: number | null = null;
    private _refreshInterval: number | null = null;

    private _selectionStrategy: IRosterSelectionStrategy;
    private _pos: { top: number; left: number; x: number; y: number } = { top: 0, left: 0, x: 0, y: 0 };
    private _isNavigateByArrow: boolean = false;

    @observable private _viewState;
    @observable private _filterState;
    @observable private _showPersonList;
    @observable private _personInfoWidthPx;
    
    constructor (props: VirtualizedRosterProps) {
        super(props);
        this._dutyStore = new DutyStore(props.type);
        this._personStore = props.personsStore;
        this._viewState = props.viewState;
        this._filterState = props.filterState;
        this._showPersonList = props.showPersonList;
        this._personInfoWidthPx = props.personInfoWidthPx;
        this._zoom = props.initialZoom ?? RosterZoom.x16;
        props.personsStore.onChangeSource(() => {
            this._dutyStore.cancel();
        });
        this._restoreViewState(this.props.viewState);
        applyDecorators(this);
        this._updateSelectionStrategy(props.selectionStrategy);
    }

    componentDidMount () {
        this._autoDisposer = autorun(() => {
            if (this._personStore.filteredPersons) {
                this._updateViewport();
            }
        });
        window.addEventListener('resize', this._onWindowResize);
        document.addEventListener('mouseup', this._onDocumentMouseUp);
        document.addEventListener('keydown', this._onDocumentKeyDown);
        signalRService.subscribeToGroup<RefreshRosterLineDto>(SignalREvents.refreshPersonRange, this._onSignalRDutyChanges, SignalRGroups.Roster, this._isPersonRelatedSignalRGroup);
        signalRService.subscribeToGroup<void>(SignalREvents.rosterHidePlanningPeriod, this._refreshOnHidePlanningPeriod, SignalRGroups.RosterPlanningPeriod);
        signalRService.subscribeToGroup<void>(SignalREvents.rosterRefresh, this._refresh);
        dutyChangeService.subscribe(this._updateDuties);
        if (this.props.autoExpandPeriod && !this._autoExpandTimeout) {
            setTimeout(() => this._checkAutoScroll(true), 0);
        }

        const time = DateTimeService.now();
        const secondsRemaining = (60 - time.getSeconds()) * 1000;
        this._timeout = window.setTimeout(() => {
            this._timeout = null;

            this.forceUpdate();
            this._refreshInterval = window.setInterval(() => {
                if (this._zoom !== RosterZoom.x16) {
                    this.forceUpdate();
                }
            }, 1000 * 60);
        }, secondsRemaining);
    }

    componentWillUnmount () {
        dutyChangeService.unsubscribe(this._updateDuties);
        signalRService.unsubscribeFromGroup(SignalREvents.refreshPersonRange, this._onSignalRDutyChanges);
        signalRService.unsubscribeFromGroup(SignalREvents.rosterHidePlanningPeriod, this._refreshOnHidePlanningPeriod);
        signalRService.subscribeToGroup<void>(SignalREvents.rosterRefresh, this._refresh);

        document.removeEventListener('keydown', this._onDocumentKeyDown);
        document.removeEventListener('mouseup', this._onDocumentMouseUp);
        window.removeEventListener('resize', this._onWindowResize);
        this._autoDisposer();

        if (this._refreshInterval) {
            window.clearInterval(this._refreshInterval);
            this._refreshInterval = null;
        }
        if (this._timeout) {
            window.clearTimeout(this._timeout);
            this._timeout = null;
        }

        if (this.props.onSaveViewState) {
            this.props.onSaveViewState({
                referenceDate: this._dateFrom,
                dateFrom: this._dateFrom,
                dateTo: this._dateTo,
                scrollTop: this._scrollTop,
                scrollLeft: this._scrollLeft,
                viewportDateFrom: this._viewportDateFrom,
                viewportDateTo: this._viewportDateTo,
            });
        }
    }

    componentDidUpdate (prevProps: VirtualizedRosterProps) {
        this._updateStatus = UpdateStatuses.Pending;
        this._viewState = this.props.viewState;
        this._showPersonList = this.props.showPersonList;
        this._personInfoWidthPx = this.props.personInfoWidthPx;
        if (this.props.type !== prevProps.type || this.props.jeppesenFileUri !== prevProps.jeppesenFileUri || this.props.generatedPlanFileUri !== prevProps.generatedPlanFileUri) {
            this._dutyStore.setType(this.props.type);
            const keepViewport = this.props.type !== RosterType.Jeppesen;
            this.refresh(keepViewport);
        }
        if (this.props.selectionStrategy !== prevProps.selectionStrategy) {
            this._updateSelectionStrategy(this.props.selectionStrategy);
        }
        if (this.props.viewState !== prevProps.viewState) {
            this._restoreViewState(this.props.viewState);
        }
        if (this.props.filterState !== prevProps.filterState) {
            this.updateFilterState(this.props.filterState);
        }

        this._updateStatus = UpdateStatuses.Ready;
        const scrollWrapper = this._scrollWrapper.current;

        if (scrollWrapper && this._scheduleEnsureRange) {
            this.ensureRangeVisible(this._scheduleEnsureRange.range, this._scheduleEnsureRange.alwaysCenter);
            this._scheduleEnsureRange = null;
        }

        if (scrollWrapper && this._scheduledRestoreScroll) {
            const { top, left } = this._scheduledRestoreScroll;
            this._scrollGrid(top, left);
            this._scheduledRestoreScroll = null;
        }

        this._positionVirtualScrolls();
        this.props.gridRef && this.props.gridRef(this._scrollWrapper);
        if (this.props.initialZoom && this.props.initialZoom !== prevProps.initialZoom) {
            this._zoom = this.props.initialZoom;
        }
    }

    render () {
        if (this.props.hidden) return null;

        const {
            onRenderHeader, onRenderFooter,
            onRenderPersonList, onRenderDaysListExtensions, showPersonList,
            height, className, showZoomControls,
            bottomInfoHeightPx, noWrapper, personsStore
        } = this.props;
        const style: React.CSSProperties = {};
        if (height) {
            style.height = height + 'px';
        }
        if (bottomInfoHeightPx) {
            style.bottom = bottomInfoHeightPx + 'px';
        }

        const wrapperClassName = ['virtualized-roster'];
        if (className) wrapperClassName.push(className);

        let zoom: string = '1';
        let canZoomIn = true;
        let canZoomOut = true;
        switch (this._zoom) {
            case RosterZoom.x1:
                zoom = '1x';
                canZoomIn = false;
                break;
            case RosterZoom.x2:
                zoom = '2x';
                break;
            case RosterZoom.x4:
                zoom = '4x';
                break;
            case RosterZoom.x8:
                zoom = '8x';
                break;
            case RosterZoom.x16:
                zoom = '16x';
                canZoomOut = false;
                break;
        }

        if (this._zoom !== RosterZoom.x16) {
            wrapperClassName.push(`person-grid_lines person-grid_${zoom}`);
        }

        const homeCalendarElement = Array.from(document.getElementsByClassName('home-calendar') as HTMLCollectionOf<HTMLElement>)[0];

        if (homeCalendarElement) {
            homeCalendarElement.style.setProperty('--cell-duty-width', `${this.cellWidthPx}px`);
        }
        const _render = (
            <>
                <div className={wrapperClassName.join(' ')} style={style}>
                    {onRenderHeader && onRenderHeader(this, this._virtualDrawOptions)}
                    <div className="person-grid-days-list" style={{ left: this.personListWidth }} ref={this._datesList}>
                        {this._renderDays()}
                    </div>
                    {onRenderDaysListExtensions && onRenderDaysListExtensions(this._datesList)}
                    {showPersonList && (
                        <div className={`person-grid-person-list ${!!personsStore.personLoadErrorHint ? 'loading-error': ''}`} style={{ width: this.personListWidth }}
                             ref={this._personList}>
                            {onRenderPersonList && onRenderPersonList(this, { ...this._virtualDrawOptions, ...{ ref: this._personList } })}
                            {this._renderPersons()}
                        </div>
                    )}
                    {this._renderGrid()}
                    {this._autoExpandTimeout &&
                        <TimeoutTimer className={this._autoExpandCssClass} style={{ marginLeft: this.personListWidth }} />}
                </div>
                {onRenderFooter && onRenderFooter(this, this._virtualDrawOptions)}
                {showZoomControls && this._renderZoom(zoom, canZoomIn, canZoomOut)}
                {!!personsStore.personLoadErrorHint && <div className="virtualized-roster-hint">{personsStore.personLoadErrorHint}</div>}
            </>
        );
        return noWrapper ? _render : <div className="virtualized-roster-wraper">{_render}</div>;
    }

    private _renderZoom (zoom: string, canZoomIn: boolean, canZoomOut: boolean) {
        return (
            <div className="zoom-wrapper">
                <div className={'zoom-out' + (canZoomOut ? '' : ' disabled')} onClick={() => this._onZoomChange(false)}>
                    <svg baseProfile="tiny" height="24px" version="1.2" viewBox="0 0 24 24" width="24px">
                        <path fill="#888" d="M18,11H6c-1.104,0-2,0.896-2,2s0.896,2,2,2h12c1.104,0,2-0.896,2-2S19.104,11,18,11z" />
                    </svg>
                </div>
                <div className="zoom-actual">
                    <span>{zoom}</span>
                </div>
                <div className={'zoom-in' + (canZoomIn ? '' : ' disabled')} onClick={() => this._onZoomChange(true)}>
                    <svg baseProfile="tiny" height="24px" version="1.2" viewBox="0 0 24 24" width="24px">
                        <path fill="#888" d="M18,10h-4V6c0-1.104-0.896-2-2-2s-2,0.896-2,2l0.071,4H6c-1.104,0-2,0.896-2,2s0.896,2,2,2l4.071-0.071L10,18  c0,1.104,0.896,2,2,2s2-0.896,2-2v-4.071L18,14c1.104,0,2-0.896,2-2S19.104,10,18,10z" />
                    </svg>
                </div>
            </div>
        );
    }

    private _renderDays () {
        const daysStyle: React.CSSProperties = {
            transform: `${this._translateLeft}`
        };
        const {
            onRenderCustomDate,
            onRenderDayExtensions,
            onRenderDayCssClassName,
            onRenderMonthExtensions,
            onRenderDailyFigures,
            onRenderRosterFilter,
            allowedDateRange,
            showSwissTime
        } = this.props;
        return <VirtualDaysList
            scrollWrapperRef={this._scrollWrapper}
            datesListRef={this._datesList}
            style={daysStyle}
            zoom={this._zoom}
            showSwissTime={showSwissTime}
            cellWidth={this.cellWidthPx}
            days={this._viewportDays}
            virtualWidth={this._virtualWidth}
            allowedDateRange={allowedDateRange}
            scrollLeft={this._scrollLeft}
            onRenderCustomDate={onRenderCustomDate}
            onRenderDayExtensions={onRenderDayExtensions}
            onRenderDayCssClassName={onRenderDayCssClassName}
            onRenderMonthExtensions={onRenderMonthExtensions}
            onRenderDailyFigures={onRenderDailyFigures}
            onRenderRosterFilter={onRenderRosterFilter}
            majorMonth={this.props.highlightMajorMonth ? this._majorMonthDay : null}
        />;
    }

    private _renderPersons (): JSX.Element | null {
        const personStore = this._personStore;
        if (!personStore.dataLoading.isCompleted) {
            return null;
        }

        const { filteredPersons } = personStore;
        const { filterState, showFocusedPersonDay, historizationStore } = this.props;
        const personItemStyle: React.CSSProperties = {
            transform: `${this._translateTop}`
        };
        const persons: JSX.Element[] = [];
        for (let i = this._viewportPersonIndexFrom; i <= this._viewportPersonIndexTo; i++) {
            if (i < 0) break;

            const person = filteredPersons[i];
            if (!person) {
                const errorMessage = `Can't get person with index ${i} \nViewportPersonIndexFrom: ${this._viewportPersonIndexFrom}.\nViewportPersonIndexTo: ${this._viewportPersonIndexTo}\n FilteredPersons list: ${JSON.stringify(filteredPersons)}..`;
                console.error(errorMessage);
                setTimeout(() => {
                    void modalService.showError(errorMessage);
                });
                continue; //was not there
            }

            const personInfo = personStore.getDetails(person.id);
            const personInfoStatus = personStore.getStatus(person.id);
            let className = '';

            if (showFocusedPersonDay && this._selectedElement && this._selectedElement.personId === person.id) {
                className += ' active';
            }
            if (!personInfo || personInfoStatus === RangeReadyStatus.Loading) {
                className += ' loading';
            }
            if (personInfoStatus === RangeReadyStatus.Error) {
                className += ' loading-error';
            }
            if (filterState && filterState.topPersonIds.indexOf(person.id) !== -1) {
                className += ' selected';
            }

            const referenceDate = this._selectedElement && new Date(this._selectedElement.dateUtc);
            persons.push(<PersonsListItem
                key={person.id}
                className={className}
                personStore={personStore}
                person={person}
                personInfo={personInfo}
                referenceDate={referenceDate}
                historizationStore={historizationStore}
                renderExtraFiguresCallback={this.props.onRenderPersonExtraInfo}
                onLicensesClick={this.props.onPersonLicenseClick}
            />);
        }

        return (
            <div className="virtual-list">
                {this._vWrapperRender()}
                <div className="virtual-list-items" style={personItemStyle}>
                    {persons}
                </div>
            </div>
        );
    }

    private _renderGrid () {
        const {
            onRenderGridExtras,
            onRenderCellDetails,
            onRenderCellExtras,
            onCellDoubleClick,
            onStartDragDuty,
            onDragOverDuty,
            onDropDuty,
            onDragEndDuty,
            allowedDateRange,
            showCapacityNumbers,
            showFocusedPersonDay,
            historizationStore,
            filterState,
            showSwissTime,
            personsStore
        } = this.props;
        const { filteredPersons } = this._personStore;
        const lines: JSX.Element[] = [];

        for (let i = this._viewportPersonIndexFrom; i <= this._viewportPersonIndexTo && i >= 0; i++) {
            const person = filteredPersons[i];
            const nextPerson = filteredPersons.length > i + 1 ? filteredPersons[i + 1] : null;
            const isBeforeActivePerson = nextPerson && this._selectedElement && this._selectedElement.personId === nextPerson.id;

            lines.push(
                <RosterLine
                    key={person.id + 'line'}
                    index={i}
                    personId={person.id}
                    personCode={person.code}
                    dutyDays={this._viewportDays}
                    rosterType={this.props.type}
                    dutyStore={this._dutyStore}
                    customSelectedClass="selected"
                    isBeforeActivePerson={isBeforeActivePerson}
                    selectedDuties={this._selectedCells}
                    focusedElement={(showFocusedPersonDay && this._selectedElement) || null}
                    allowedDateRange={allowedDateRange}
                    showCapacityNumbers={showCapacityNumbers}
                    onDoubleClick={onCellDoubleClick}
                    onRenderCellDetails={onRenderCellDetails}
                    onRenderCellExtras={onRenderCellExtras}
                    copiedDuties={this._copiedDuties}
                    historizeLastDate={historizationStore?.getHistorizeLastDate(filterState?.functionGroupId || null, person.department || null)}
                    showSwissTime={showSwissTime}
                    onStartDragDuty={onStartDragDuty}
                    onDragOverDuty={onDragOverDuty}
                    onDragEndDuty={onDragEndDuty}
                    onDropDuty={onDropDuty}
                    showDutyAsFight={this._zoom !== RosterZoom.x16}
                />
            );
        }

        const dutyItemStyle: React.CSSProperties = {
            transform: `${this._translateTop}${this._translateLeft}`,
            width: `${this._gridClientWidth}px`
        };
        return (
            <div ref={this._scrollWrapper} className={`person-grid-duty-list ${isIOS ? 'customScrollbar' : ''} ${!!personsStore.personLoadErrorHint ? 'loading-error': ''}`}
                 style={{ left: this.personListWidth }} tabIndex={0} onScroll={this._onRosterScroll}
                 onMouseDown={this._onGridMouseDown} onMouseOver={this._onGridMouseOver}>
                <div className="virtual-list">
                    {this._vWrapperRender()}
                    <VirtualHorizontalExpander virtualWidth={this._virtualWidth} />
                    <div className="virtual-list-items" style={dutyItemStyle}>
                        {lines}
                    </div>
                </div>
                <ReactResizeDetector handleWidth onResize={this._onRosterResize} />
                {onRenderGridExtras && onRenderGridExtras(this, { ...this._virtualDrawOptions, ...{ ref: this._scrollWrapper } })}
            </div>
        );
    }

    @computed
    public get cellWidthPx (): number {
        return this._zoom === RosterZoom.x16 ? Sizes.dutyWidths : 2 * getDayWidth(this._zoom!);
    }

    @computed
    private get _isSelectedRowInView () {
        const selectedPersonIndex = this._personStore.filteredPersons.findIndex((p) => !!this._selectedElement && p.id === this._selectedElement.personId);
        return selectedPersonIndex >= this._viewportPersonIndexFrom && selectedPersonIndex <= this._viewportPersonIndexTo;
    }

    private get _virtualWidth (): number {
        const total = DateTimeService.diffDays(this._dateTo, this._dateFrom) + 1;
        return total * this.cellWidthPx;
    }

    private get _virtualHeight (): number {
        const { filteredPersons } = this._personStore;
        return filteredPersons.length * Sizes.dutyHeight + (this._isSelectedRowInView ? this.props.lineInfoHeight || 0 : 0);
    }

    private get _virtualDrawOptions () {
        const scrollTop = this._scrollTop;
        let viewportTop = scrollTop - Sizes.dutyHeight * Extensions.getDecimal(scrollTop / Sizes.dutyHeight);

        if (!!this.props.lineInfoHeight && this.selectedRowIndex !== null && this.selectedRowIndex > -1) {
            const hStandart = Sizes.dutyHeight;
            const hSelected = Sizes.dutyHeight + this.props.lineInfoHeight;
            const heightBeforeSelectedElement = this.selectedRowIndex * hStandart;

            if (scrollTop >= heightBeforeSelectedElement && scrollTop <= heightBeforeSelectedElement + hSelected) {
                const nStandart = Math.floor(heightBeforeSelectedElement / hStandart);
                viewportTop = nStandart * hStandart;
            }
            if (scrollTop > heightBeforeSelectedElement + hSelected) {
                const nStandart = Math.floor((scrollTop - hSelected) / hStandart);
                viewportTop = nStandart * hStandart + hSelected;
            }
        }

        return {
            viewportDays: this._viewportDays,
            virtualWidth: this._virtualWidth,
            virtualHeight: this._virtualHeight,
            viewportLeft: this._scrollLeft - this.cellWidthPx * Extensions.getDecimal(this._scrollLeft / this.cellWidthPx),
            viewportTop: viewportTop
        };
    }

    private _vWrapperRender (): JSX.Element | null {
        const fakeStyle = {
            height: this._virtualHeight,
            clip: ''
        };
        return <div className="virtual-list-vwrapper" style={fakeStyle} />;
    }

    private get _translateLeft (): string {
        return ` translateX(${this._virtualDrawOptions.viewportLeft}px)`;
    }

    private get _translateTop (): string {
        return ` translateY(${this._virtualDrawOptions.viewportTop}px)`;
    }

    @computed
    public get personListWidth (): string {
        if (this._showPersonList) {
            const width = Sizes.personWidth + (this._personInfoWidthPx || 0);
            return `${width}px`;
        }
        return '0';
    }

    private _positionVirtualScrolls () {
        const datesList = this._datesList.current;
        if (datesList) {
            datesList.scrollLeft = this._scrollLeft;
        }

        const personsList = this._personList.current;
        if (personsList) {
            personsList.scrollTop = this._scrollTop;
        }
    }

    @action.bound
    private _onWindowResize () {
        this._updateViewport();
    }

    @action.bound
    private _updateViewport (loadData: boolean = true) {
        if (this._updateStatus === UpdateStatuses.NeedToUpdate) return;

        this._positionVirtualScrolls();
        const scrollTop = this._scrollTop;
        const scrollLeft = this._scrollLeft;

        const grid = this._scrollWrapper.current;
        if (!grid) {
            setTimeout(() => this._updateViewport(loadData), 0);
            return;
        }
        const height = grid.offsetHeight;
        const viewportPersonsCount = Math.floor(height / Sizes.dutyHeight || 0);
        let viewportPersonIndex = Math.floor(scrollTop / Sizes.dutyHeight);

        if (!!this.props.lineInfoHeight && this.selectedRowIndex !== null && this.selectedRowIndex > -1) {
            const hStandart = Sizes.dutyHeight;
            const hSelected = Sizes.dutyHeight + this.props.lineInfoHeight;
            const heightBeforeSelectedElement = this.selectedRowIndex * hStandart;

            if (scrollTop >= heightBeforeSelectedElement && scrollTop <= heightBeforeSelectedElement + hSelected) {
                viewportPersonIndex = this.selectedRowIndex;
            }
            if (scrollTop > heightBeforeSelectedElement + hSelected) {
                const nStandart = Math.floor((scrollTop - hSelected) / hStandart);
                viewportPersonIndex = nStandart + 1;
            }
        }

        this._viewportPersonIndexFrom = viewportPersonIndex;
        this._viewportPersonIndexTo = viewportPersonIndex + viewportPersonsCount + 1;
        if (this._viewportPersonIndexTo >= this._personStore.filteredPersons.length) {
            this._viewportPersonIndexTo = this._personStore.filteredPersons.length - 1;
        }

        const viewportPersonsRange = {
            from: this._viewportPersonIndexFrom,
            to: this._viewportPersonIndexTo
        };

        const width = grid.offsetWidth;
        const viewportDaysCount = Math.floor(width / this.cellWidthPx || 0) + 1;
        const viewportDutyStartIndex = Math.floor(scrollLeft / this.cellWidthPx);

        const viewportDateFrom = DateTimeService.addDays(this._dateFrom, viewportDutyStartIndex);
        let viewportDateTo = DateTimeService.addDays(viewportDateFrom, viewportDaysCount);
        if (viewportDateTo > this._dateTo) {
            viewportDateTo = this._dateTo;
        }
        const viewportDatesRange = {
            from: viewportDateFrom,
            to: viewportDateTo
        };
        if (viewportDateFrom > viewportDateTo) {
            viewportDatesRange.from = viewportDatesRange.to;
        }
        if (viewportDatesRange.from !== this._viewportDateFrom) {
            this._viewportDateFrom = viewportDatesRange.from;
        }
        if (viewportDatesRange.to !== this._viewportDateTo) {
            this._viewportDateTo = viewportDatesRange.to;
        }

        const viewportDates: Date[] = [];
        let currentDate = viewportDateFrom;
        while (currentDate <= viewportDateTo) {
            viewportDates.push(currentDate);
            currentDate = DateTimeService.addDays(currentDate, 1);
        }

        if (!ArrayHelper.isSameDateArray(this._viewportDays, viewportDates)) {
            this._viewportDays = viewportDates;
            this._updateMajorMonth();
        }

        const virtualRange = {
            from: this._dateFrom,
            to: this._dateTo
        };

        if (this._personStore.filteredPersons.length && loadData !== false) {
            this._dutyStore.loadViewport(this._personStore.filteredPersons, viewportPersonsRange, viewportDatesRange, virtualRange);
            this._personStore.loadViewport(viewportPersonsRange);
            this.props.onPersonRangeLoad && this.props.onPersonRangeLoad(viewportPersonsRange, null, LoadMode.Viewport);
        }
        this.props.onDateRangeLoad && this.props.onDateRangeLoad(viewportDatesRange, virtualRange, LoadMode.Viewport);
    }

    @action
    private _waitTimeout (direction: 'left' | 'right' | 'clear', immediate?: boolean) {
        if (this._autoExpandTimeout) {
            clearTimeout(this._autoExpandTimeout);
            this._autoExpandTimeout = null;
        }
        if (direction === 'clear') return;

        this._autoExpandCssClass = direction;
        this._autoExpandTimeout = window.setTimeout(
            () => {
                if (direction === 'left') this.toPrevMonth(true);
                if (direction === 'right') this.toNextMonth(true);
                this._autoExpandTimeout = null;
            },
            immediate ? 0 : 700
        );
    }

    @action
    private _checkAutoScroll (immediate?: boolean) {
        const grid = this._scrollWrapper.current;
        if (!grid) return;

        if (!grid.scrollLeft) {
            this._waitTimeout('left', immediate);
        } else if (grid.scrollWidth <= Math.ceil(grid.scrollLeft + grid.offsetWidth)) {
            this._waitTimeout('right', immediate);
        } else {
            this._waitTimeout('clear');
        }
    }

    @action.bound
    private _onRosterScroll (event: React.UIEvent<Element>) {
        const target = event.target as HTMLElement;
        if (target !== this._scrollWrapper.current) return;

        const shouldUpdateConfig = this._scrollTop !== target.scrollTop || this._scrollLeft !== target.scrollLeft;
        this._scrollTop = target.scrollTop;
        this._scrollLeft = target.scrollLeft;

        if (this.props.autoExpandPeriod) {
            this._checkAutoScroll();
        }

        this._updateViewport();
        this.props.onScroll && this.props.onScroll(this._scrollTop, this._scrollLeft, shouldUpdateConfig);
    }

    @action.bound
    private _onRosterResize(width?: number) {
        const shouldUpdateConfig = this._gridClientWidth !== 0;
        if (typeof width === 'number') {
            this._gridClientWidth = width;
        }
        this._updateViewport();
        this.props.onScroll && this.props.onScroll(this._scrollTop, this._scrollLeft, shouldUpdateConfig);
    }

    @action
    public toNextMonth (keepScroll?: boolean) {
        const grid = this._scrollWrapper.current;
        if (!grid) return;

        const dateTo = this._dateTo;
        const viewportDateTo = this._viewportDateTo;

        const endOfMonthTo = DateTimeService.date(dateTo.getUTCFullYear(), dateTo.getUTCMonth() + 1, 0);
        const isVirtualToMonth = DateTimeService.isSameDate(dateTo, endOfMonthTo);

        const monthsInView = DateTimeService.diffMonths(this._dateFrom, this._dateTo);
        let waitTillUpdate = true;
        if (viewportDateTo) {
            if (viewportDateTo < dateTo) {
                waitTillUpdate = false;
            } else if (!isVirtualToMonth) {
                this._dateTo = endOfMonthTo;
            } else {
                const endOfNextMonth = DateTimeService.date(dateTo.getUTCFullYear(), dateTo.getUTCMonth() + 2, 0);
                this._dateTo = endOfNextMonth;
            }
            if (keepScroll) {
                this._dateFrom = DateTimeService.addMonths(DateTimeService.toFirstDayOfMonth(this._dateTo), -monthsInView);
            }

            if (keepScroll) {
                const newViewportDateTo = DateTimeService.date(this._dateTo.getUTCFullYear(), this._dateTo.getUTCMonth(), 0);
                const totalDaysTo = DateTimeService.diffDays(newViewportDateTo, this._dateFrom) + 1;
                const lengthToPx = totalDaysTo * this.cellWidthPx;
                this._scheduleScrollGrid(null, lengthToPx - grid.clientWidth, waitTillUpdate);
            } else {
                const endOfViewportMonth = DateTimeService.date(viewportDateTo.getUTCFullYear(), viewportDateTo.getUTCMonth() + 1, 0);

                let newViewportDateTo: Date;
                if (DateTimeService.isSameDate(endOfViewportMonth, viewportDateTo)) {
                    newViewportDateTo = DateTimeService.date(endOfViewportMonth.getUTCFullYear(), endOfViewportMonth.getUTCMonth() + 2, 0);
                } else {
                    newViewportDateTo = endOfViewportMonth;
                }
                const totalDaysTo = DateTimeService.diffDays(newViewportDateTo, this._dateFrom) + 1;
                const lengthToPx = totalDaysTo * this.cellWidthPx;
                this._scheduleScrollGrid(null, lengthToPx - grid.clientWidth, waitTillUpdate);
            }
        }
    }

    @action
    public toPrevMonth (keepScroll?: boolean) {
        let dateFrom = this._dateFrom;
        let viewportDateFrom = this._viewportDateFrom;

        let waitTillUpdate = false;
        let dateIndex = 0;

        if (viewportDateFrom) {
            if (viewportDateFrom.getUTCDate() > 1) {
                viewportDateFrom = DateTimeService.toFirstDayOfMonth(viewportDateFrom);
                dateIndex = DateTimeService.getDateIndexInRange(viewportDateFrom, dateFrom);
                this._viewportDateFrom = viewportDateFrom;
            } else if (dateFrom.getUTCMonth() !== viewportDateFrom.getUTCMonth()) {
                viewportDateFrom = DateTimeService.toFirstDayOfMonth(viewportDateFrom);
                viewportDateFrom = DateTimeService.addMonths(viewportDateFrom, -1);
                dateIndex = DateTimeService.getDateIndexInRange(viewportDateFrom, dateFrom);
            } else {
                dateFrom = DateTimeService.addMonths(dateFrom, -1);
                dateFrom = DateTimeService.toFirstDayOfMonth(dateFrom);
                if (keepScroll) {
                    const monthsInView = DateTimeService.diffMonths(this._dateFrom, this._dateTo);
                    this._dateTo = DateTimeService.toLastDayOfMonth(DateTimeService.addMonths(dateFrom, monthsInView));
                }
                this._dateFrom = dateFrom;
                dateIndex = 0;
                waitTillUpdate = true;
            }
        }

        if (keepScroll) {
            const monthDayStart = DateTimeService.toFirstDayOfMonth(this._dateFrom);
            const monthDayEnd = DateTimeService.toLastDayOfMonth(this._dateFrom);
            const newScroll = this._scrollLeft + (DateTimeService.diffDays(monthDayEnd, monthDayStart) + 1) * this.cellWidthPx;
            this._scheduleScrollGrid(null, newScroll, waitTillUpdate);
        } else {
            this._scheduleScrollGrid(null, dateIndex * this.cellWidthPx, waitTillUpdate);
        }
    }

    @action
    public ensureRangeVisible (range: Range<Date>, alwaysCenter?: boolean | null) {
        const scrollWrapper = this._scrollWrapper.current;
        if (!scrollWrapper) {
            this._scheduleEnsureRange = { range: range, alwaysCenter: alwaysCenter };
            return true;
        }

        if (!alwaysCenter && this._viewportDateFrom && this._viewportDateTo) {
            if (this._viewportDateFrom < range.from && range.to < this._viewportDateTo) return true;
        }

        const days = DateTimeService.diffDays(range.to, range.from) + 1;
        const daysInView = scrollWrapper.offsetWidth / this.cellWidthPx;

        if (days > daysInView - 2) {
            return this.navigateTo(null, DateTimeService.addDays(range.from, -1), NavigateBehavior.topLeft);
        } else {
            const middleDate = DateTimeService.startOfDate(new Date((range.from.getTime() + range.to.getTime()) / 2));
            return this.navigateTo(null, middleDate, NavigateBehavior.center);
        }
    }

    @action
    public navigateTo (personId: string | null, date: Date | null, behavior: NavigateBehavior = NavigateBehavior.center, selectCell?: boolean) {
        const scrollWrapper = this._scrollWrapper.current;
        if (!scrollWrapper) {
            return false;
        }

        this._navigateInternal(scrollWrapper, personId, date, behavior);

        if (selectCell) {
            const dateUtc = this._selectedElement && this._selectedElement.dateUtc;
            const selectedDate = date ? date.getTime() : dateUtc;
            if (personId && selectedDate) {
                const cell: RosterElement = {
                    personId: personId,
                    dateUtc: selectedDate
                };
                this.selectedCells = [cell];
                this.setFocusedElement(cell);
            } else {
                this.selectedCells = [];
            }
        }

        return true;
    }

    private _navigateInternal (scrollWrapper: HTMLDivElement, personId: string | null, date: Date | null, behavior: NavigateBehavior = NavigateBehavior.center) {
        const height = scrollWrapper.offsetHeight;
        const width = scrollWrapper.offsetWidth;

        const personIndex = this._personStore.filteredPersons.findIndex((p) => p.id === personId);
        if (personId && personIndex === -1) {
            return false;
        }

        let waitTilUpdate: boolean | null = null;

        const scrollTop = personId ? Math.max(0, Math.round(Sizes.dutyHeight * (personIndex + 1) - (behavior === NavigateBehavior.center ? height / 2 + Sizes.dutyHeight / 2 : 0))) : this._scrollTop;
        if (scrollTop > scrollWrapper.scrollHeight) {
            waitTilUpdate = true;
        }

        let scrollLeft: number = this._scrollLeft;
        if (date) {
            const daysInView = width / this.cellWidthPx;
            const dayStart = DateTimeService.addDays(date, -daysInView);
            const dayEnd = DateTimeService.addDays(date, daysInView);

            if (dayStart < this._dateFrom) {
                this._dateFrom = DateTimeService.toFirstDayOfMonth(dayStart);
                waitTilUpdate = true;
            }
            if (dayEnd > this._dateTo) {
                this._dateTo = DateTimeService.toLastDayOfMonth(dayEnd);
                waitTilUpdate = true;
            }
            scrollLeft = Math.round(DateTimeService.getDateIndexInRange(date, this._dateFrom) * this.cellWidthPx - (behavior === NavigateBehavior.center ? width / 2 - this.cellWidthPx / 2 : 0));
        }

        this._scheduleScrollGrid(scrollTop, scrollLeft, waitTilUpdate);
    }

    @action
    public scrollToPerson (personId: string) {
        const personIndex = this._personStore.filteredPersons.findIndex((p) => p.id === personId);
        if (personIndex === -1) {
            return false;
        }

        const scrollTop = Sizes.dutyHeight * personIndex;
        this._scheduleScrollGrid(scrollTop, null, true);
    }

    @computed
    public get viewState (): RosterViewState {
        const result: RosterViewState = {
            dateFrom: this._dateFrom,
            dateTo: this._dateTo,
            viewportDateFrom: this._viewportDateFrom,
            viewportDateTo: this._viewportDateTo,
            selectedPersonsMap: Array.from(this._selectedPersonIdsMap.entries()),
            selectedDate: this._selectedElement ? new Date(this._selectedElement.dateUtc) : null,
            scrollTop: this._scrollTop,
            scrollLeft: this._scrollLeft,
            referenceDate: this._viewState.referenceDate
        };
        return result;
    }

    @action
    public updateFilterState(newState?: HomeFiltersState | null) {
        this._filterState = newState;
    }

    @action
    private _restoreViewState (viewState: RosterViewState) {
        this._selectedPersonIdsMap = new Map(viewState.selectedPersonsMap);
        const selectedPersonId = this.getSelectedPersonIdForCurrentFunction();
        if (viewState.selectedDate && selectedPersonId) {
            this.setFocusedElement({ personId: selectedPersonId, dateUtc: viewState.selectedDate.getTime() });
        }

        if (viewState.keepFullRange && viewState.dateFrom && viewState.dateTo) {
            this._dateFrom = viewState.dateFrom;
            this._dateTo = viewState.dateTo;

            let scrollLeft = viewState.scrollLeft || 0;
            if ((viewState.scrollLeft || null) === null && viewState.viewportDateFrom) {
                scrollLeft = this.cellWidthPx * DateTimeService.diffDays(viewState.viewportDateFrom, viewState.dateFrom);
            }
            this._scrollLeft = scrollLeft;
        } else {
            const baseDate = DateTimeService.toFirstDayOfMonth(viewState.viewportDateFrom || viewState.referenceDate);
            const dateFrom = DateTimeService.toFirstDayOfMonth(DateTimeService.addMonths(baseDate, -2));
            const dateTo = DateTimeService.toLastDayOfMonth(DateTimeService.addMonths(baseDate, 2));

            let scrollLeft = viewState.scrollLeft || 0;
            if (dateFrom && viewState.dateFrom) {
                scrollLeft -= this.cellWidthPx * DateTimeService.diffDays(dateFrom, viewState.dateFrom);
            }

            this._scrollLeft = scrollLeft;
            this._dateFrom = dateFrom;
            this._dateTo = dateTo;
        }
        this._scrollTop = viewState.scrollTop || 0;

        this._updateViewport();
        if (viewState.viewportCenterRange) {
            this.ensureRangeVisible(viewState.viewportCenterRange, true);
        } else {
            this._scheduledRestoreScroll = { top: this._scrollTop, left: this._scrollLeft };
        }
    }

    public getSelectedPersonIdForCurrentFunction() {
        if (!this._filterState) return null;

        const { functionGroupId, functionId } = this._filterState;
        let selectedPersonId = this._personStore.filteredPersons.length ? this._personStore.filteredPersons[0].id : null;

        const funcIdKey = `${functionGroupId}|${functionId}`;
        const personIdFromMap = this._selectedPersonIdsMap.get(funcIdKey);
        const existsWithCurrentFilters = this._personStore.filteredPersons.find(p => p.id === personIdFromMap);
        if (existsWithCurrentFilters && personIdFromMap) {
            selectedPersonId = personIdFromMap;
        }

        return selectedPersonId;
    }

    @action.bound
    private _refreshOnHidePlanningPeriod () {
        setTimeout(async () => {
            this.refresh();
            //add random delay to avoid DDoS
        }, Math.random() * 1000 * 30);
    }

    private _refresh = () => this.refresh();

    @action.bound
    private _onSignalRDutyChanges (_: string, data: RefreshRosterLineDto) {
        const { dateFromUtc, dateToUtc, personId, isValidation, functionGroupId, functionId } = data;
        if (dateFromUtc && dateToUtc) {
            const periodsIntersects = this._dateFrom <= dateToUtc && dateFromUtc <= this._dateTo;
            if (!periodsIntersects || !this._viewportDateFrom || !this._viewportDateTo) return;

            const refreshRange = { from: dateFromUtc, to: dateToUtc };
            const viewportRange = { from: this._viewportDateFrom, to: this._viewportDateTo };
            const viewportPersonIds = { from: this._viewportPersonIndexFrom, to: this._viewportPersonIndexTo + 1 };

            personId && this._dutyStore.invalidate(personId, refreshRange);
            personId && this.props.onPersonRangeLoad?.(viewportPersonIds, [personId], LoadMode.RefreshVisible);
            if (this._personStore.filteredPersons.find(x => x.id == personId) || !personId) {
                this.props.onDateRangeLoad?.(viewportRange, null, LoadMode.RefreshVisible, { range: refreshRange, personIds: personId ? [personId] : [], isValidation, functionGroupId, functionId });
            }
        }
    }

    @action
    public refresh (keepDataDuringRefresh: boolean = false) {
        this.resetCopiedDuties();
        this._dutyStore.clear(keepDataDuringRefresh);
        this.props.onDateRangeLoad && this.props.onDateRangeLoad(null, null, LoadMode.Clean);
        this.props.onPersonRangeLoad && this.props.onPersonRangeLoad(null, null, LoadMode.Clean);
        this.props.onPlanningDutyPeriodStatusLoad && this.props.onPlanningDutyPeriodStatusLoad();
        this._updateViewport();
    }

    @action
    public reloadDailyData () {
        this.props.onDateRangeLoad && this.props.onDateRangeLoad(null, null, LoadMode.Clean);
        this._updateViewport();
    }

    @action.bound
    private async _updateDuties (changeEvent: DutyChangeEvent): Promise<void> {
        if (changeEvent.personsRange) {
            const {
                range: { from, to },
                personIds
            } = changeEvent.personsRange;
            this.props.onDateRangeLoad && this.props.onDateRangeLoad({ from: from, to: to }, {
                from: this._dateFrom,
                to: this._dateTo
            }, LoadMode.Refresh);
            this.props.onPersonRangeLoad && this.props.onPersonRangeLoad(null, personIds, LoadMode.Refresh);
            await this._dutyStore.loadDuties({ from: from, to: to }, personIds);
        }

        if (changeEvent.duties) {
            let minUpdateDate: Date | null = null;
            let maxUpdateDate: Date | null = null;
            const personIds: string[] = [];
            changeEvent.duties.forEach((d) => {
                minUpdateDate = DateTimeService.min(minUpdateDate, d.date);
                maxUpdateDate = DateTimeService.max(maxUpdateDate, d.date);
                if (personIds.indexOf(d.personId) === -1) personIds.push(d.personId);
            });
            if (minUpdateDate && maxUpdateDate) {
                this._dutyStore.setDuties(changeEvent.duties);
                this.props.onDateRangeLoad && this.props.onDateRangeLoad({
                    from: minUpdateDate,
                    to: maxUpdateDate
                }, { from: this._dateFrom, to: this._dateTo }, LoadMode.Refresh, { range: { from: minUpdateDate, to: maxUpdateDate }, personIds: [...changeEvent.duties.map(x => x.personId)], isValidation: false });
                this.props.onPersonRangeLoad && this.props.onPersonRangeLoad(null, personIds, LoadMode.Refresh);
            }
        }
    }

    private _updateSelectionStrategy (strategy?: SelectionStrategyConstructor) {
        const strategyConstructor: SelectionStrategyConstructor = strategy || RosterSelectionStrategyDefault;
        this._selectionStrategy = new strategyConstructor(this, this._scrollWrapper);
        this._selectionStrategy.updateDomSelection(this.selectedCells);
    }

    public getDuty (personId: string, date: Date) {
        const personMap = this._dutyStore.dutyList.get(personId);
        return (personMap && personMap.get(date.getTime())) || null;
    }

    public focus () {
        this._scrollWrapper.current && this._scrollWrapper.current.focus();
    }

    public scrollTop () {
        if (this._scrollWrapper.current) this._scrollWrapper.current.scrollTop = 0;
    }

    public resetCopiedDuties () {
        this._copiedDuties = [];
    }

    @computed
    public get selectedDuties (): DutyItem[] {
        const selectedDuties: DutyItem[] = [];
        for (const cell of this._selectedCells) {
            const personDutyMap = this._dutyStore.dutyList.get(cell.personId);
            if (personDutyMap) {
                const personDuty = personDutyMap.get(cell.dateUtc);
                if (personDuty) {
                    selectedDuties.unshift(personDuty);
                }
            }
        }

        return selectedDuties;
    }

    @computed
    public get viewportDateFrom () {
        return this._viewportDateFrom;
    }

    @computed
    public get viewportDateTo () {
        return this._viewportDateTo;
    }

    @computed
    public get selectedRowIndex (): number | null {
        const selectedElement = this._selectedElement;
        return selectedElement && this._personStore.filteredPersons.findIndex((p) => p.id === selectedElement.personId);
    }

    @computed
    public get selectedRowNumber (): number | null {
        const selectedElement = this._selectedElement;
        return selectedElement && this._personStore.filteredPersons.findIndex((p) => p.id === selectedElement.personId) - this._viewportPersonIndexFrom;
    }

    @computed
    public get selectedColumnIndex (): number | null {
        const selectedElement = this._selectedElement;
        return selectedElement && DateTimeService.getDateIndexInRange(new Date(selectedElement.dateUtc), this._dateFrom);
    }

    @computed
    public get datesRange (): number {
        return Math.round((this._dateTo.getTime() - this._dateFrom.getTime()) / (24 * 60 * 60 * 1000));
    }

    @computed
    public get copiedDuties (): DutyItem[] {
        return this._copiedDuties;
    }

    @computed
    get selectedCells (): RosterElement[] {
        return this._selectedCells;
    }

    set selectedCells (cells: RosterElement[]) {
        this._selectedCells = cells;
        this._selectionStrategy.updateDomSelection(cells);
        this.props.onCellSelected && this.props.onCellSelected(this._selectedCells);
    }

    private _scheduleScrollGrid (top: number | null, left: number | null, waitTillUpdate?: boolean | null) {
        const scrollWrapper = this._scrollWrapper.current;
        let behavior: 'scroll' | 'timeout' | 'update' = 'timeout';

        if (waitTillUpdate === true) {
            behavior = 'update';
        }
        if (waitTillUpdate === false) {
            behavior = 'scroll';
        }
        if (behavior === 'timeout') {
            if (scrollWrapper && this._updateStatus !== UpdateStatuses.Pending) {
                behavior = 'scroll';
            } else {
                behavior = 'update';
            }
        }

        if (behavior === 'scroll') {
            // just scroll, no new range is expected
            this._scrollGrid(top, left);
        } else if (behavior === 'update') {
            if (left !== null) {
                this._scrollLeft = left;
            }

            this.forceUpdate(() => {
                this._scrollGrid(top, left);
            });
        }
    }

    private _scrollGrid (top: number | null, left: number | null) {
        const gridDutyList = this._scrollWrapper.current;
        if (gridDutyList) {
            let updated = false;
            if (left !== null) {
                const isScrollVisible = gridDutyList.scrollWidth > gridDutyList.clientWidth;
                if (isScrollVisible) {
                    if (gridDutyList.scrollLeft !== left) {
                        gridDutyList.scrollLeft = left;
                        updated = true;
                    }
                } else {
                    this._onRosterResize(gridDutyList.clientWidth);
                }
            }
            if (top !== null) {
                if (gridDutyList.scrollTop !== top) {
                    gridDutyList.scrollTop = top;
                    updated = true;
                }
            }

            if (!updated) {
                if (top) this._scrollTop = top;
                if (left) this._scrollLeft = left;
                this._updateViewport();
            }
        }
    }

    @action.bound
    private _onGridMouseDown (event: React.MouseEvent<HTMLDivElement>) {
        if (event.shiftKey) {
            if (event.button !== 0) return;
            const containerRef = this._scrollWrapper.current;

            if (containerRef) {
                this._pos = {
                    // The current scroll
                    left: containerRef.scrollLeft ?? 0,
                    top: containerRef.scrollTop ?? 0,
                    // Get the current mouse position
                    x: event.clientX,
                    y: event.clientY
                };

                containerRef.style.userSelect = 'none';
                document.addEventListener('mousemove', this._containerOnMouseMove);
                document.addEventListener('mouseup', this._onContainerMouseUp);
            }
        } else if (event.altKey) {
            const sourceDutyElement = (event.target as HTMLDivElement).parentElement;
            const isAllowDragDuty = this.props.onStartDragDuty?.(sourceDutyElement);
            if (!!isAllowDragDuty) return;
        } else {
            this._selectionStrategy.mouseDownHandler(event);
        }
    }

    private _containerOnMouseMove = (e: MouseEvent) => {
        const containerRef = this._scrollWrapper.current;

        // How far the mouse has been moved
        const dx = e.clientX - this._pos.x;
        const dy = e.clientY - this._pos.y;

        // Scroll the element
        if (containerRef) {
            containerRef.scrollTop = this._pos.top - dy;
            const newScrollLeft = this._pos.left - dx;
            this._pos.x = e.clientX;

            if (newScrollLeft <= 0) {
                this._viewportDateFrom = this._dateFrom;
                flushSync(() => this.toPrevMonth(true));
                this._pos.left = containerRef.scrollLeft;
                return;
            }

            if (containerRef.scrollWidth <= Math.ceil(newScrollLeft + containerRef.offsetWidth)) {
                this._viewportDateTo = this._dateTo;
                flushSync(() => this.toNextMonth(true));
                this._pos.left = containerRef.scrollLeft;
                return;
            }

            this._pos.left = newScrollLeft;
            containerRef.scrollLeft = newScrollLeft;
        }
    };

    private _onContainerMouseUp = () => {
        const containerRef = this._scrollWrapper.current;

        document.removeEventListener('mousemove', this._containerOnMouseMove);
        document.removeEventListener('mouseup', this._onContainerMouseUp);
        if (containerRef) {
            containerRef.style.removeProperty('user-select');
        }
    };

    @action
    public setFocusedElement (element: RosterElement) {
        this._selectedElement = element;
        this._savePersonForCurrentCrewFunction(element.personId);
        this._savePersonForArrowsNavigation(element.personId);
        this.props.onCellFocused && this.props.onCellFocused(element);
        this._scrollWrapper.current && this._scrollWrapper.current.focus(); //need to fix ctrl+z change input effect because by default after reload focus element is body
    }

    private _savePersonForCurrentCrewFunction(personId: string) {
        const personInfo = this._personStore.getPerson(personId);
        const personFunctionId = personInfo?.planningFunctionId ?? personInfo?.functionId;
        if (!personFunctionId || !this._filterState) return;

        const { functionGroupId } = this._filterState;
        if (functionGroupId !== FunctionGroupIds.Fcm && functionGroupId !== FunctionGroupIds.Fse) {
            const funcIdKey = `${functionGroupId}|${personFunctionId}`;
            this._selectedPersonIdsMap.set(funcIdKey, personId);
        }

        const funcGroupIdKey = `${functionGroupId}|${functionGroupId}`;
        this._selectedPersonIdsMap.set(funcGroupIdKey, personId);
    }

    private _savePersonForArrowsNavigation(personId: string) {
        if (this._isNavigateByArrow || this.props.type !== RosterType.Full) return;

        const storedPersons = appStore.getValue<StoredPersons>('selectedPersons');
        const personIdsForArrowNavigation = storedPersons?.personIds || [];
        const currentIndex = storedPersons?.currentIndex || 0;
        const lastIndex = personIdsForArrowNavigation.length ? personIdsForArrowNavigation.length - 1 : 0;
        const currentPersonId = personIdsForArrowNavigation.length ? personIdsForArrowNavigation[currentIndex] : null;
        const maxStoredPersonsCount = 30;

        if (currentPersonId === personId) {
            return;
        }

        if (currentIndex < lastIndex) {
            personIdsForArrowNavigation.splice(currentIndex + 1);
        }

        if (personIdsForArrowNavigation.length >= maxStoredPersonsCount) {
            personIdsForArrowNavigation.splice(0, 1);
        }

        personIdsForArrowNavigation.push(personId);

        const newStoredPersons: StoredPersons = {
            currentIndex: personIdsForArrowNavigation.length - 1,
            personIds: personIdsForArrowNavigation
        };
        appStore.setValue<StoredPersons>('selectedPersons', newStoredPersons);
    }

    @action.bound
    private _onDocumentMouseUp () {
        this._selectionStrategy.documentMouseUpHandler();
    }

    @action.bound
    private _onGridMouseOver (event: React.MouseEvent<HTMLDivElement>) {
        this._selectionStrategy.mouseMoveHandler(event);
    }

    @action
    public copyDuties (initiator: ActionInitiator) {
        if (this.selectedDuties && this.props.onCanCopyDuties?.(initiator, this.selectedDuties)) {
            this._copiedDuties = this.selectedDuties;
        }
    }

    @action
    public pasteDuties (initiator: ActionInitiator) {
        if (!!this._copiedDuties.length) {
            this.props.onPasteDuties?.(initiator, this._copiedDuties);
        }
    }

    @action.bound
    public pasteDraggedDuty (duty: RosterElement, initiator: ActionInitiator) {
        this.selectedCells = [duty];
        this.pasteDuties(initiator);
        this.setFocusedElement(duty);
    }

    private _navigateToPreviousStoredPerson() {
        const storedPersons = appStore.getValue<StoredPersons>('selectedPersons');
        if (!storedPersons || this.props.type !== RosterType.Full) return;

        const personIdsForArrowNavigation = storedPersons.personIds || [];
        const previousPersonIndex = storedPersons.currentIndex - 1;
        if (previousPersonIndex < 0) return;

        storedPersons.currentIndex = previousPersonIndex;
        appStore.setValue<StoredPersons>('selectedPersons', storedPersons);

        const previousPersonId = personIdsForArrowNavigation[previousPersonIndex];
        void this._navigateToStoredPerson(previousPersonId);
    }

    private _navigateToNextStoredPerson() {
        const storedPersons = appStore.getValue<StoredPersons>('selectedPersons');
        if (!storedPersons || this.props.type !== RosterType.Full) return;

        const personIdsForArrowNavigation = storedPersons.personIds || [];
        const nextPersonIndex = storedPersons.currentIndex + 1;
        if (nextPersonIndex > personIdsForArrowNavigation.length - 1) return;

        storedPersons.currentIndex = nextPersonIndex;
        appStore.setValue<StoredPersons>('selectedPersons', storedPersons);

        const nextPersonId = personIdsForArrowNavigation[nextPersonIndex];
        void this._navigateToStoredPerson(nextPersonId);
    }

    private async _navigateToStoredPerson(personId: string) {
        const currentDate = this._selectedElement ? new Date(this._selectedElement.dateUtc) : new Date();
        this._isNavigateByArrow = true;
        await this.props.onNavigateToPerson?.(personId, currentDate);
        this._isNavigateByArrow = false;
    }

    @action.bound
    private _onDocumentKeyDown (event: KeyboardEvent) {
        const target = event.target as Element;
        if (this._scrollWrapper.current !== target) return;

        if (event.ctrlKey && event.keyCode === KeyboardKeys.C) {
            this.copyDuties(ActionInitiator.Keyboard);
        } else if (event.keyCode === KeyboardKeys.ESCAPE) {
            this.resetCopiedDuties();
        } else if (event.ctrlKey && event.keyCode === KeyboardKeys.V) {
            this.pasteDuties(ActionInitiator.Keyboard);
        } else if (event.ctrlKey && event.key === KeyboardCodes.ArrowLeft) {
            this._navigateToPreviousStoredPerson();
        } else if (event.ctrlKey && event.key === KeyboardCodes.ArrowRight) {
            this._navigateToNextStoredPerson();
        }
    }

    private get _isPersonRelatedSignalRGroup () {
        switch (this.props.type) {
            case RosterType.RequestToolFlight:
            case RosterType.RequestToolGeneral:
            case RosterType.RequestToolOff:
            case RosterType.RequestToolVacCabin:
            case RosterType.RequestToolVacCockpit:
            case RosterType.RequestToolFreelance:
                return true;
            default:
                return false;
        }
    }

    private _onZoomChange (isZoomIn: boolean) {
        if (isZoomIn) {
            switch (this._zoom) {
                case RosterZoom.x2:
                    this._zoom = RosterZoom.x1;
                    break;
                case RosterZoom.x4:
                    this._zoom = RosterZoom.x2;
                    break;
                case RosterZoom.x8:
                    this._zoom = RosterZoom.x4;
                    break;
                case RosterZoom.x16:
                    this._zoom = RosterZoom.x8;
                    break;
            }
        } else {
            switch (this._zoom) {
                case RosterZoom.x1:
                    this._zoom = RosterZoom.x2;
                    break;
                case RosterZoom.x2:
                    this._zoom = RosterZoom.x4;
                    break;
                case RosterZoom.x4:
                    this._zoom = RosterZoom.x8;
                    break;
                case RosterZoom.x8:
                    this._zoom = RosterZoom.x16;
                    break;
            }
        }
        const scrollWrapper = this._scrollWrapper.current;
        if (!scrollWrapper || !this._viewportDateFrom || !this._viewportDateTo) {
            return false;
        }

        const middleDate = DateTimeService.startOfDate(new Date((this._viewportDateFrom.getTime() + this._viewportDateTo.getTime()) / 2));
        const scrollLeft: number = Math.round(DateTimeService.getDateIndexInRange(middleDate, this._dateFrom) * this.cellWidthPx - (scrollWrapper.offsetWidth / 2 - this.cellWidthPx / 2));

        this._scheduleScrollGrid(this._scrollTop, scrollLeft, true);
        this.props.onZoom?.(this._zoom!);
    }

    private _updateMajorMonth () {
        const majorMonthDay = this._getMajorMonth(this._viewportDays);
        if (this._majorMonthDay?.getTime() !== majorMonthDay?.getTime()) {
            this._majorMonthDay = majorMonthDay;
            if (majorMonthDay) {
                this.props.onMajorMonthChange?.(majorMonthDay);
            }
        }
    }

    private _getMajorMonth (viewportDays: Date[]) {
        const monthGroups = ArrayHelper
            .groupBy(viewportDays, el => `${el.getFullYear()}${el.getMonth()}`)
            .map(group => ({
                day: group.values[0],
                daysCount: group.values.length
            }));

        if (!monthGroups.length) {
            return null;
        }
        if (monthGroups.length === 1) {
            return monthGroups[0].day;
        }
        if (monthGroups.length > 2) {
            return monthGroups[1].day;
        }
        return monthGroups[0].daysCount > monthGroups[1].daysCount ? monthGroups[0].day : monthGroups[1].day;
    }
}
